import { AxiosError } from 'axios';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, TaskSearchConditions } from '../../../api/model';
import { useGetTasksMasters, usePostTasksSearch } from '../../../api/tasks/tasks';
import { useDefaultOptions } from '../../../hooks/api';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import ErrorComponent from '../../atoms/ErrorComponent';
import NoDataComponent from '../../atoms/NoDataComponent';
import WaitComponent from '../../atoms/WaitComponent';
import { ModelUserTaskList } from './ModelUserTaskList';
import { TaskList } from './TaskList';
import { TaskSearch } from './TaskSearch';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC')}</title>
    </Helmet>
  );
}

function HomePage() {
  // 検索条件部分が開いているかどうか
  const [openSearch, setOpenSearch] = useState(true);

  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 表示グループ（初期はUser）
  const [displayGroup, setDisplayGroup] = useState(false);

  // 検索条件取得API
  const {
    data: mastersData,
    isLoading: mastersIsLoading,
    isError: mastersIsError,
    error: mastersError,
  } = useGetTasksMasters(options);

  // 初期検索条件（セッションストレージに保存されている）
  const [defaultConditions] = useState<TaskSearchConditions>(
    sessionStorageUtils.get<TaskSearchConditions>('Tasks.SearchConditions', {})
  );

  // 検索 API 情報
  const refConditions = useRef(sessionStorageUtils.get<TaskSearchConditions>('Tasks.SearchConditions', {}));

  // 検索用API
  const {
    data,
    isLoading,
    isError,
    error,
    isSuccess,
    mutateAsync: searchAsync,
  } = usePostTasksSearch<AxiosError<ErrorInfo>, unknown>(options);

  // 初期表示処理（条件を保存し検索する）
  useEffect(() => {
    const postTasks = async () => {
      const defaultData = { ...defaultConditions, include_completed: true };
      await searchAsync(
        { data: defaultData },
        {
          // APIアクセスが成功した場合
          onSuccess: () => {
            sessionStorageUtils.set('Tasks.SearchConditions', defaultData);
            refConditions.current = defaultData;
          },
        }
      );
    };
    console.log(`初期表示  ${new Date().toLocaleTimeString()}`);
    void postTasks();
  }, [defaultConditions, searchAsync]);

  // 手動検索処理（条件を保存し検索する）
  const setConditionsAndSearch = useCallback(
    (next: TaskSearchConditions) => {
      const postTasks = async () => {
        // APIアクセスが成功した場合
        const onSuccess = () => {
          sessionStorageUtils.set('Tasks.SearchConditions', next);
          refConditions.current = next;
        };

        // API アクセスの実行
        console.log(`★手動更新ON  ${new Date().toLocaleTimeString()}`);
        await searchAsync({ data: next }, { onSuccess });
      };
      void postTasks();
    },
    [searchAsync]
  );

  // APIからデータを取得する関数
  const postTaskSearchFetch = useCallback(async () => {
    await searchAsync({ data: refConditions.current });
  }, [refConditions, searchAsync]);

  // 自動更新処理（5分ごとに再取得）
  useEffect(() => {
    if (isSuccess) {
      const timerId = setTimeout(() => {
        console.log(`▼自動更新ON  ${new Date().toLocaleTimeString()}`);
        void postTaskSearchFetch();
      }, 1000 * 60 * 5);
      console.log(`set TimerID ${String(timerId)}`);

      // クリーンアップ（検索が成功するごとに、タイマーオブジェクトが新規で生成され続ける）
      return () => {
        console.log(`TimerID ${String(timerId)} has canceled.`);
        clearTimeout(timerId);
      };
    }
    return undefined;
  }, [isSuccess, postTaskSearchFetch]);

  // 待ち状態
  if (mastersIsLoading) {
    return (
      <div id="main">
        <Title />
        <WaitComponent />
      </div>
    );
  }

  // エラー
  if (mastersIsError) {
    return (
      <div id="main">
        <Title />
        {mastersIsError && <ErrorComponent error={mastersError} />}
      </div>
    );
  }

  // データが取得できない場合
  if (
    mastersData.data.destinations == null ||
    mastersData.data.model_names == null ||
    mastersData.data.destinations == null ||
    mastersData.data.production_statuses == null ||
    mastersData.data.model_users == null ||
    mastersData.data.production_divisions == null
  ) {
    return (
      <div id="main">
        <Title />
        <NoDataComponent />
      </div>
    );
  }

  return (
    <div id="main">
      <Title />
      <section>
        <ModelUserTaskList />

        <h3 className="trig">
          <span>
            {t('検索条件')}
            {/* 検索条件の開閉 */}
            <button
              type="button"
              className="text-button"
              onClick={(e) => {
                e.preventDefault();
                setOpenSearch((pre) => !pre);
              }}
            >
              <i className={`fa-solid ${openSearch ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>

        <TaskSearch
          openSearch={openSearch}
          defaultConditions={defaultConditions}
          modelNames={mastersData.data.model_names}
          destinations={mastersData.data.destinations}
          productionStatuses={mastersData.data.production_statuses}
          productionDevisions={mastersData.data.production_divisions}
          treeCompanies={mastersData.data.model_users}
          conditionsRef={refConditions}
          displayGroup={displayGroup}
          setConditionsAndSearch={setConditionsAndSearch}
        />
        <TaskList
          data={data}
          isLoading={isLoading}
          isError={isError}
          error={error}
          displayGroup={displayGroup}
          setDisplayGroup={setDisplayGroup}
          conditionsRef={refConditions}
          setConditionsAndSearch={setConditionsAndSearch}
        />
      </section>
    </div>
  );
}

export default HomePage;
